import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import * as Papa from 'papaparse';

// Define interface for species data
interface Species {
  taxonomy_id: string;
  species_name: string;
  common_name: string;
}

// Define interface for API response
interface PPINetworkResponse {
  data: PPINetworkEdge[];
  execution_time: number;
  message: string;
  status: number;
}

// Define interface for edge data
interface PPINetworkEdge {
  Filename: string;
  Method: string;
  ProteinA: string;
  ProteinA_ID_type: string;
  ProteinB: string;
  ProteinB_ID_type: string;
  Reference: string;
  Source: string;
  Species: string;
  id: number;
}

// Define interfaces for Cytoscape data structures
interface CytoscapeNode {
  data: {
    id: string;
    node_name: string;
    network_type: string;
    [key: string]: any;
  };
}

interface CytoscapeEdge {
  data: {
    id: string;
    source: string;
    target: string;
    network_type: string;
    [key: string]: any;
  };
  classes: string;
}

interface CytoscapeData {
  style: Array<{
    selector: string;
    css: {
      [key: string]: any;
    };
  }>;
  elements: {
    nodes: CytoscapeNode[];
    edges: CytoscapeEdge[];
  };
}

@Component({
  selector: 'app-ppi-network',
  templateUrl: './ppi-network.component.html',
  styleUrls: ['./ppi-network.component.scss'],
})
export class PpiNetworkComponent implements OnInit {
  ELEMENT_DATA: CytoscapeData | null = null;
  DETAILS: any;
  dataType = 'ppi';
  targetProtein = 'Q9ZWT2'; // Default protein ID
  showCyto = false;
  proteinType = 'UniprotID'; // Default protein type (options: UniprotID, IntAct, LocusID, STRING)
  searchLevel = 2; // Default search level for network neighbors
  species = '3702'; // Default species ID (Arabidopsis thaliana)
  apiUrl = 'https://ptmsite.info:5004/api/ppi_network/';
  
  // Species data
  speciesList: Species[] = [];
  speciesMap: Map<string, Species> = new Map<string, Species>();
  taxonomyIds: string[] = [
    '112509', '15368', '2711', '29760', '3055', '3218', '34305', 
    '3562', '3635', '3694', '3702', '3847', '3880', '3888', 
    '39946', '39947', '4081', '4097', '4113', '4498', '4513', 
    '4530', '4558', '4565', '4577'
  ];
  
  // State management
  isLoading = false;
  hasError = false;
  errorMessage = '';
  
  // Track unique proteins for node creation
  uniqueProteins = new Set<string>();
  nodes: CytoscapeNode[] = [];
  edges: CytoscapeEdge[] = [];

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadSpeciesData();
    this.getData(this.targetProtein);
  }
  
  /**
   * Load species data from CSV file
   */
  loadSpeciesData(): void {
    const csvPath = 'assets/species.csv';
    this.http.get(csvPath, { responseType: 'text' }).subscribe(
      (data) => {
        Papa.parse(data, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            this.speciesList = result.data.map((row: any) => ({
              taxonomy_id: row['Taxonomy ID'],
              species_name: row['Species Name'],
              common_name: row['Common Name']
            }));
            
            // Create a map for quick lookup
            this.speciesList.forEach(species => {
              this.speciesMap.set(species.taxonomy_id, species);
            });
            
            console.log('Loaded species data:', this.speciesList.length);
          },
          error: (err) => console.error('Error parsing species CSV:', err)
        });
      },
      (error) => console.error('Error fetching species CSV:', error)
    );
  }
  
  /**
   * Get species name for a taxonomy ID
   */
  getSpeciesName(taxonomyId: string): string {
    const species = this.speciesMap.get(taxonomyId);
    if (species) {
      return `${species.species_name} (${species.common_name})`;
    }
    return taxonomyId; // Return the ID if species not found
  }
  
  /**
   * Get placeholder text based on the selected protein type
   */
  getPlaceholder(): string {
    switch (this.proteinType) {
      case 'UniprotID':
        return 'Q9ZWT2';
      case 'IntAct':
        return 'EBI-28';
      case 'LocusID':
        return 'AT4G37990';
      case 'STRING':
        return '3702.AT4G37990.1';
      default:
        return 'Enter protein ID';
    }
  }

  /**
   * Validate input and trigger search
   */
  fn_search(): void {
    // Trim whitespace from protein ID
    this.targetProtein = this.targetProtein.trim();
    
    if (!this.targetProtein) {
      this.hasError = true;
      this.errorMessage = 'Please enter a protein ID';
      return;
    }
    
    this.getData(this.targetProtein);
  }

  /**
   * Get data from the new API endpoint
   * @param proteinValue The protein ID value
   */
  getData(proteinValue: string): void {
    this.targetProtein = proteinValue;
    
    // Build URL with required and optional parameters
    let url = `${this.apiUrl}?protein=${encodeURIComponent(proteinValue)}`;
    
    // Add optional parameters if they have values
    if (this.searchLevel) {
      url += `&searchLevel=${this.searchLevel}`;
    }
    
    if (this.species) {
      url += `&species=${this.species}`;
    }
    
    // Reset states
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';
    this.showCyto = false;
    
    console.log('Fetching data from:', url);
    
    this.fetchJson(url).subscribe({
      next: (response: PPINetworkResponse) => {
        this.isLoading = false;
        console.log('API Response:', response);
        
        if (response && response.status === 1 && response.data) {
          if (response.data.length > 0) {
            // Process the edge data from the API
            this.processNetworkData(response.data, this.targetProtein);
          } else {
            // No data found for this protein
            this.hasError = true;
            this.errorMessage = `No protein-protein interactions found for ${proteinValue} with the selected search level and species. Try adjusting the search level or species.`;
            this.showCyto = false;
          }
        } else {
          console.error('Error fetching PPI network data:', response);
          // Handle empty response or error
          this.hasError = true;
          this.errorMessage = response?.message || `Failed to fetch protein interaction data for ${proteinValue}. Please verify the protein ID is correct.`;
          this.showCyto = false;
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.hasError = true;
        
        // Provide more specific error messages based on HTTP status codes
        if (error.status === 404) {
          this.errorMessage = 'The API endpoint could not be found. Please contact support.';
        } else if (error.status === 500) {
          this.errorMessage = 'The server encountered an error processing your request. Please try again later.';
        } else if (error.status === 0) {
          this.errorMessage = 'Cannot connect to the server. Please check your internet connection and try again.';
        } else {
          this.errorMessage = `Error connecting to the server (${error.status}). Please try again later.`;
        }
        
        console.error('Error calling PPI network API:', error);
        this.showCyto = false;
      }
    });
  }

  /**
   * Fetch JSON data from a URL
   * @param url The URL to fetch data from
   * @returns Observable of the response
   */
  fetchJson(url: string): Observable<PPINetworkResponse> {
    console.log('Fetching from URL:', url);
    const options = {
      responseType: 'json' as const
    };

    return this.http.get<PPINetworkResponse>(url, options).pipe(
      map((response) => {
        console.log('Response received:', response);
        return response;
      })
    );
  }

  /**
   * Process the network data from the API response
   * @param edgeData The edge data from the API
   * @param targetProtein The target protein ID
   */
  processNetworkData(edgeData: PPINetworkEdge[], targetProtein: string): void {
    // Reset data structures
    this.uniqueProteins.clear();
    this.nodes = [];
    this.edges = [];
    
    // Always add the target protein to ensure it's in the network
    this.uniqueProteins.add(targetProtein);
    
    // Process edge data to extract unique proteins and create edges
    edgeData.forEach(edge => {
      // Add both proteins to the unique set
      this.uniqueProteins.add(edge.ProteinA);
      this.uniqueProteins.add(edge.ProteinB);
      
      // Create edge data
      this.edges.push({
        data: {
          id: `${edge.ProteinA}-${edge.ProteinB}-${edge.id}`,
          source: edge.ProteinA,
          target: edge.ProteinB,
          network_type: 'ppi',
          Method: edge.Method,
          Reference: edge.Reference,
          Source: edge.Source,
          Species: edge.Species,
          Filename: edge.Filename
        },
        classes: 'tooltip'
      });
    });
    
    // Create nodes from unique proteins
    this.uniqueProteins.forEach(protein => {
      this.nodes.push({
        data: {
          id: protein,
          node_name: protein,
          network_type: 'ppi'
        }
      });
    });
    
    // Create the network visualization data
    this.createNetworkVisualization(targetProtein);
  }

  /**
   * Create the network visualization data structure
   * @param targetProtein The target protein ID
   */
  createNetworkVisualization(targetProtein: string): void {
    // Create the visualization data structure
    this.ELEMENT_DATA = {
      style: [
        {
          selector: 'node',
          css: {
            content: 'data(node_name)',
            'border-color': '#99ccff',
            'border-opacity': 1,
            'border-width': '1px',
            'background-color': '#99ccff',
          },
        },
        {
          // Highlight the target protein in red
          selector: `node[id = "${targetProtein}"]`,
          css: {
            content: 'data(node_name)',
            'border-color': '#cc0000',
            'border-opacity': 1,
            'border-width': '1px',
            'background-color': '#cc0000',
          },
        },
        {
          selector: 'edge',
          css: {
            'target-arrow-shape': 'triangle',
            'line-color': '#666666',
            color: '#000000',
          },
        },
      ],
      elements: {
        nodes: this.nodes,
        edges: this.edges,
      },
    };
    
    // Show the network if we have data
    this.showCyto = this.nodes.length > 0;
  }
}
